import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import useOutsideClick from '@/hooks/useOutsideClick';
import useModalAnimation from '@/hooks/useModalAnimation';
import { useLockBodyScroll } from '@/hooks';
import { Icons } from '@/utils/Icons';
import { useGetBannersQuery } from '@/store/api/contentApi';

interface WelcomeBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const swiperProps = {
  modules: [Autoplay],
  slidesPerView: 1,
  loop: true,
  autoplay: { delay: 3000, disableOnInteraction: false },
  className: 'w-full h-full',
};

function WelcomeBannerModal({ isOpen, onClose }: WelcomeBannerModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { animate, handleClose } = useModalAnimation({ isOpen, onClose });
  // modalRef.current is null while closed, so an unguarded outside-click would
  // treat every page click as "outside" and close whichever modal is active.
  const handleOutsideClick = useCallback(() => {
    if (isOpen) handleClose();
  }, [isOpen, handleClose]);
  useOutsideClick(modalRef, handleOutsideClick);

  const { data: webData } = useGetBannersQuery(
    { platform: 'WebBanner', type: 'WellcomeBanner' },
    { skip: !isOpen }
  );
  const { data: appData } = useGetBannersQuery(
    { platform: 'AppBanner', type: 'WellcomeBanner' },
    { skip: !isOpen }
  );

  const webBanners = webData?.data?.WebBanner?.WellcomeBanner ?? [];
  const appBanners = appData?.data?.AppBanner?.WellcomeBanner ?? [];
  const hasBanners = webBanners.length > 0 || appBanners.length > 0;

  // Only lock scroll once we actually have banners to show — otherwise the
  // modal locks scroll but renders nothing, and there's no close button to
  // release the lock, leaving the page stuck.
  useLockBodyScroll(isOpen && hasBanners);

  if (!isOpen || !hasBanners) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
      <div
        ref={modalRef}
        className={`relative rounded-t-[10px] md:rounded-[20px] overflow-hidden bg-white dark:bg-darkPanelBg border border-[#fec733] dark:border-[#808080] shadow-[0px_4px_7px_0px_#80808040] w-[300px] aspect-[230/410] md:w-[820px] md:aspect-[746/536] transform transition-all duration-300 ease-out ${
          animate ? 'translate-y-0 opacity-100' : '-translate-y-1/2 opacity-0'
        }`}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-black/40 p-1.5 text-white transition hover:bg-black/60"
        >
          <Icons.close size={18} />
        </button>

        {/* desktop / web banner: source asset 746x536 */}
        <div className="hidden md:block h-full">
          <Swiper {...swiperProps}>
            {webBanners.map((banner) => (
              <SwiperSlide key={banner.id}>
                <img
                  src={banner.image}
                  alt={banner.name}
                  className="w-full h-full object-cover"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* mobile / app banner: source asset 230x410 */}
        <div className="md:hidden h-full">
          <Swiper {...swiperProps}>
            {appBanners.map((banner) => (
              <SwiperSlide key={banner.id}>
                <img
                  src={banner.image}
                  alt={banner.name}
                  className="w-full h-full object-cover"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default WelcomeBannerModal;
